import React, { useEffect } from "react";
import Tiptap from "@/components/TipTap/TipTap";
import GenerateBriefForm from "./GenerateBriefForm";
import { GenerateBriefFormProps } from "./GenerateBriefForm";
import { useForm } from "@inertiajs/react";
import BriefGenerationLoading from "@/components/loading/BriefGenerationLoading";
import Input from "@/components/inputs/Input";
import Button from "@/components/buttons/Button";
import { Link } from "@inertiajs/react";
import { ArrowLeft } from "lucide-react";
import FormActions from "@/components/buttons/FormActions";

interface PreviewProps extends GenerateBriefFormProps {
  generated_brief: string;
  title: string;
}

const Preview: React.FC<PreviewProps> = ({
  generated_brief,
  title,
  ...formProps
}) => {
  const { data, setData, post, processing, errors } = useForm({
    title: title,
    body: "",
    ...formProps, // Include formProps in the initial data
  });

  // Set title when it arrives as deferred prop
  useEffect(() => {
    setData("title", title);
  }, [title]);

  const handleEditorContentChange = (content: any) => {
    setData("body", content);
  };

  const handleEditorReady = (editor: any) => {
    setData("body", editor.getJSON());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(`/briefs`);
  };

  return (
    <>
      <div className="flex w-full flex-col items-start justify-start gap-10">
        <div className="flex w-full flex-col gap-5">
          <Link href={`/briefs`}>
            <div className="flex flex-row gap-2">
              <ArrowLeft />
              Back to all briefs
            </div>
          </Link>
          {generated_brief && (
            <div className="flex w-full flex-row items-baseline justify-between gap-5">
              <h1 className="font-serif text-2xl font-bold lg:text-4xl">
                Review and save your brief
              </h1>
            </div>
          )}
        </div>

        {!generated_brief ? (
          <>
            <BriefGenerationLoading {...formProps} />
          </>
        ) : (
          <>
            <p>
              Here's your AI-generated sync brief. You can edit or add to it
              now, and you'll be able to make more changes after saving.
            </p>
            <form
              onSubmit={handleSubmit}
              className="flex w-full flex-col items-start justify-start gap-10"
            >
              <Input
                type="text"
                value={data.title}
                onChange={(e) => setData("title", e.target.value)}
                placeholder="Brief title"
                errors={errors.title}
                label="Title"
              />

              <div className="flex w-full flex-col gap-2">
                <Tiptap
                  content={generated_brief}
                  setEditorContent={handleEditorContentChange}
                  onEditorReady={handleEditorReady}
                  initialiseAsEditable={true}
                />
                {errors.body && (
                  <div className="text-orange-600">{errors.body}</div>
                )}
              </div>
              <FormActions>
                <Button
                  type="primary"
                  text="Save"
                  disabled={processing}
                  className="self-end"
                />
              </FormActions>
            </form>
            <h1 className="font-serif text-xl font-bold lg:text-2xl">
              Not 100% satisfied?
            </h1>
            <GenerateBriefForm
              {...formProps}
              formDescription="You can update any fields below and generate a new brief. Just a heads up: this will replace your current brief, so the new one might be quite different!"
              cta="Regenerate Brief"
            />
          </>
        )}
      </div>
    </>
  );
};

export default Preview;
